import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, api } from "@/App";
import { Button } from "@/components/ui/button";
import { X, MessageCircle, Calendar, Crown, CheckCircle2, Sparkles, Trophy, ChevronRight } from "lucide-react";
import { fireTrophyConfetti } from "@/lib/confetti";
import { useLocale } from "@/i18n/LocaleContext";

const SHOWN_PATHS = ["/discover", "/chat", "/meetups", "/premium-offers", "/favorites", "/leaderboard", "/credits"];

export default function TestDriveTour() {
  const { user } = useAuth();
  const { t } = useLocale();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [status, setStatus] = useState(null);
  const [expanded, setExpanded] = useState(() => !localStorage.getItem("luxe_tour_seen"));
  const celebrated = useRef(false);

  const onTourPage =
    SHOWN_PATHS.includes(pathname) || (pathname.startsWith("/profile/") && !pathname.includes("/edit"));

  const fetchStatus = useCallback(() => {
    if (!user || user.needs_onboarding || user.is_ai) return;
    api.get("/test-drive/status").then((res) => setStatus(res.data)).catch(() => {});
  }, [user?.user_id, user?.needs_onboarding]);

  useEffect(() => {
    if (onTourPage) fetchStatus();
  }, [fetchStatus, pathname, onTourPage]);

  useEffect(() => {
    const onRestart = () => {
      setExpanded(true);
      fetchStatus();
    };
    window.addEventListener("tour-restart", onRestart);
    return () => window.removeEventListener("tour-restart", onRestart);
  }, [fetchStatus]);

  const steps = status?.steps || {};
  const allDone = steps.chat && steps.meetup && steps.offer;

  useEffect(() => {
    if (!status || status.dismissed || allDone || !onTourPage) return;
    const id = setInterval(fetchStatus, 12000);
    return () => clearInterval(id);
  }, [status?.dismissed, allDone, fetchStatus, onTourPage]);

  useEffect(() => {
    if (allDone && expanded && !celebrated.current) {
      celebrated.current = true;
      fireTrophyConfetti();
    }
  }, [allDone, expanded]);

  if (!user || user.needs_onboarding || user.is_ai || !status || status.dismissed || !status.companion || !onTourPage) {
    return null;
  }

  const companion = status.companion;
  const avatar = companion.photos?.[0] || companion.picture;
  const doneCount = [steps.chat, steps.meetup, steps.offer].filter(Boolean).length;

  const handleDismiss = async () => {
    setStatus((prev) => ({ ...prev, dismissed: true }));
    try {
      await api.post("/test-drive/dismiss");
    } catch (e) { /* non-blocking */ }
  };

  const collapse = () => {
    localStorage.setItem("luxe_tour_seen", "1");
    setExpanded(false);
  };

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="fixed bottom-20 md:bottom-6 left-4 z-40 flex items-center gap-2 pl-1.5 pr-4 py-1.5 rounded-full bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium shadow-lg shadow-violet-900/40 transition-colors"
        data-testid="test-drive-pill"
      >
        {avatar ? (
          <img src={avatar} alt={companion.name} className="w-7 h-7 rounded-full object-cover" />
        ) : (
          <Sparkles className="w-5 h-5 ml-1" />
        )}
        {t("tour.tryWith")} {companion.name}
        <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-white/20">{doneCount}/3</span>
      </button>
    );
  }

  const stepDefs = [
    {
      key: "chat",
      done: steps.chat,
      icon: MessageCircle,
      title: t("tour.step1Title"),
      desc: t("tour.step1Desc").replace("{name}", companion.name),
      cta: () =>
        status.conversation_id
          ? navigate(`/chat/${status.conversation_id}`)
          : navigate(`/profile/${companion.user_id}?action=message`),
    },
    {
      key: "meetup",
      done: steps.meetup,
      icon: Calendar,
      title: t("tour.step2Title"),
      desc: t("tour.step2Desc"),
      cta: () => navigate(`/profile/${companion.user_id}?action=meetup`),
    },
    {
      key: "offer",
      done: steps.offer,
      icon: Crown,
      title: t("tour.step3Title"),
      desc: steps.offer && !steps.offer_completed
        ? t("tour.step3DescSent")
        : t("tour.step3Desc"),
      cta: () =>
        steps.offer && !steps.offer_completed
          ? navigate("/premium-offers")
          : navigate(`/profile/${companion.user_id}?action=offer`),
    },
  ];

  return (
    <div
      className="fixed bottom-20 md:bottom-6 left-4 z-40 w-[320px] rounded-2xl border border-violet-500/30 bg-[#151022]/95 backdrop-blur-xl shadow-2xl shadow-violet-950/50 overflow-hidden"
      data-testid="test-drive-card"
    >
      <div className="flex items-center gap-3 px-4 pt-4 pb-3">
        {avatar && <img src={avatar} alt={companion.name} className="w-10 h-10 rounded-full object-cover" />}
        <div className="flex-1 min-w-0">
          <p className="font-semibold font-['Outfit'] text-sm">{t("tour.tryWith")} {companion.name}</p>
          <p className="text-xs text-muted-foreground">{t("tour.subtitle")}</p>
        </div>
        <button type="button" onClick={collapse} className="text-muted-foreground hover:text-white p-1" title="Minimize" data-testid="tour-minimize-btn">
          <ChevronRight className="w-4 h-4 rotate-90" />
        </button>
        <button type="button" onClick={handleDismiss} className="text-muted-foreground hover:text-white p-1" title="Dismiss tour" data-testid="tour-dismiss-btn">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="h-1 bg-white/5">
        <div className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all duration-500" style={{ width: `${(doneCount / 3) * 100}%` }} />
      </div>

      {allDone ? (
        <div className="p-5 text-center" data-testid="tour-complete">
          <Trophy className="w-10 h-10 text-amber-400 mx-auto mb-2" />
          <p className="font-semibold mb-1">{t("tour.doneTitle")}</p>
          <p className="text-xs text-muted-foreground mb-4">
            {t("tour.doneDesc")}
          </p>
          <Button
            className="w-full bg-violet-600 hover:bg-violet-500"
            onClick={() => {
              handleDismiss();
              navigate("/discover");
            }}
            data-testid="tour-finish-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Browse real members
          </Button>
        </div>
      ) : (
        <div className="p-3 space-y-2">
          {stepDefs.map((s, i) => (
            <div
              key={s.key}
              className={`flex items-center gap-3 p-2.5 rounded-xl border ${
                s.done ? "border-green-500/20 bg-green-500/5" : "border-white/10 bg-white/[0.03]"
              }`}
              data-testid={`tour-step-${s.key}`}
            >
              {s.done ? (
                <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" />
              ) : (
                <span className="w-5 h-5 shrink-0 rounded-full border border-white/25 text-[10px] flex items-center justify-center text-muted-foreground">{i + 1}</span>
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${s.done ? "text-green-300 line-through decoration-green-500/40" : ""}`}>{s.title}</p>
                {!s.done && <p className="text-[11px] text-muted-foreground truncate">{s.desc}</p>}
              </div>
              {!s.done && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 px-2.5 border-violet-500/40 text-violet-300 hover:bg-violet-500/15 shrink-0"
                  onClick={s.cta}
                  data-testid={`tour-cta-${s.key}`}
                >
                  <s.icon className="w-3.5 h-3.5 mr-1" /> {t("tour.go")}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
